import { useState } from 'react';
import { useSystemMetrics } from '@/hooks/useQueries';
import { Modal } from '@/components/ui/Modal/Modal';
import './SystemResourceWidget.css';

interface SystemResourceWidgetProps {
  collapsed: boolean;
}

function getStatusColor(percent: number): string {
  if (percent >= 90) return 'var(--ui-color-error)';
  if (percent >= 70) return 'var(--ui-color-warning)';
  return 'var(--ui-color-success)';
}

function getOverallStatus(
  cpu: number,
  mem: number,
  disk: number
): 'good' | 'warn' | 'critical' {
  const max = Math.max(cpu, mem, disk);
  if (max >= 90) return 'critical';
  if (max >= 70) return 'warn';
  return 'good';
}

function SystemResourceWidget({ collapsed }: SystemResourceWidgetProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const { data, isLoading, isError } = useSystemMetrics();

  if (isLoading || isError || !data) {
    if (collapsed) {
      return (
        <div className="sysres-widget sysres-widget--collapsed">
          <div className="sysres-icon-wrapper">
            <ServerIcon />
            <span className="sysres-status-dot sysres-status-dot--loading" />
          </div>
        </div>
      );
    }
    return (
      <div className="sysres-widget">
        <div className="sysres-loading">
          <ServerIcon />
          <span className="sysres-loading-text">
            {isError ? 'Unavailable' : 'Loading...'}
          </span>
        </div>
      </div>
    );
  }

  const status = getOverallStatus(
    data.cpu_percent,
    data.memory_percent,
    data.disk_percent
  );

  if (collapsed) {
    return (
      <>
        <button
          className="sysres-widget sysres-widget--collapsed"
          onClick={() => setModalOpen(true)}
          title="System Resources"
        >
          <div className="sysres-icon-wrapper">
            <ServerIcon />
            <span
              className={`sysres-status-dot sysres-status-dot--${status}`}
            />
          </div>
        </button>
        <DetailModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          data={data}
        />
      </>
    );
  }

  return (
    <>
      <button
        className="sysres-widget"
        onClick={() => setModalOpen(true)}
        title="Click for details"
      >
        <MetricBar label="CPU" percent={data.cpu_percent} />
        <MetricBar
          label="MEM"
          percent={data.memory_percent}
          detail={`${data.memory_used_gb}/${data.memory_total_gb} GB`}
        />
        <MetricBar
          label="DISK"
          percent={data.disk_percent}
          detail={`${data.disk_used_gb}/${data.disk_total_gb} GB`}
        />
      </button>
      <DetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        data={data}
      />
    </>
  );
}

function MetricBar({
  label,
  percent,
  detail,
}: {
  label: string;
  percent: number;
  detail?: string;
}) {
  const color = getStatusColor(percent);
  return (
    <div className="sysres-metric">
      <div className="sysres-metric-header">
        <span className="sysres-metric-label">{label}</span>
        <span className="sysres-metric-value">{Math.round(percent)}%</span>
      </div>
      <div className="sysres-bar-track">
        <div
          className="sysres-bar-fill"
          style={{
            width: `${Math.min(percent, 100)}%`,
            backgroundColor: color,
          }}
        />
      </div>
      {detail && <span className="sysres-metric-detail">{detail}</span>}
    </div>
  );
}

function DetailModal({
  open,
  onClose,
  data,
}: {
  open: boolean;
  onClose: () => void;
  data: NonNullable<ReturnType<typeof useSystemMetrics>['data']>;
}) {
  return (
    <Modal open={open} onClose={onClose} title="System Resources" size="sm">
      <div className="sysres-detail">
        <DetailRow
          label="CPU Usage"
          percent={data.cpu_percent}
          detail={`${data.cpu_percent.toFixed(1)}%`}
        />
        <DetailRow
          label="Memory"
          percent={data.memory_percent}
          detail={`${data.memory_used_gb} GB / ${data.memory_total_gb} GB (${data.memory_percent.toFixed(1)}%)`}
        />
        <DetailRow
          label="Disk"
          percent={data.disk_percent}
          detail={`${data.disk_used_gb} GB / ${data.disk_total_gb} GB (${data.disk_percent.toFixed(1)}%)`}
        />
        {data.load_average.some((v) => v > 0) && (
          <div className="sysres-detail-row">
            <span className="sysres-detail-label">Load Average</span>
            <span className="sysres-detail-value">
              {data.load_average.map((v) => v.toFixed(2)).join(' / ')}
            </span>
            <span className="sysres-detail-sub">1 min / 5 min / 15 min</span>
          </div>
        )}
      </div>
    </Modal>
  );
}

function DetailRow({
  label,
  percent,
  detail,
}: {
  label: string;
  percent: number;
  detail: string;
}) {
  const color = getStatusColor(percent);
  return (
    <div className="sysres-detail-row">
      <span className="sysres-detail-label">{label}</span>
      <div className="sysres-detail-bar-track">
        <div
          className="sysres-detail-bar-fill"
          style={{
            width: `${Math.min(percent, 100)}%`,
            backgroundColor: color,
          }}
        />
      </div>
      <span className="sysres-detail-value">{detail}</span>
    </div>
  );
}

function ServerIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="20" height="8" x="2" y="2" rx="2" ry="2" />
      <rect width="20" height="8" x="2" y="14" rx="2" ry="2" />
      <line x1="6" x2="6.01" y1="6" y2="6" />
      <line x1="6" x2="6.01" y1="18" y2="18" />
    </svg>
  );
}

export default SystemResourceWidget;
